<script setup>
import { storeToRefs } from 'pinia';
import { ref, computed, watch } from 'vue';
import SessionForm from './components/SessionForm.vue';
import { useSessionStore } from '@stores';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import { useI18n } from 'vue-i18n';
import { deleteSession } from '@services/sessionService';
import { useAuthStore } from '@stores';
import { useRoute } from 'vue-router';

const authStore = useAuthStore();
const sessionStore = useSessionStore();
const { sessionsList } = storeToRefs(sessionStore);
const route = useRoute();
const isModerator = computed(() => authStore.user.roles == 'Moderator');
const isSuperAdmin = computed(() => authStore.user.roles.includes('Superadmin'));
const confirm = useConfirm();
const toast = useToast();
const { t } = useI18n({ useScope: 'global' });
const createModalShow = ref(false);
const editForm = ref(false);
const editSessionId = ref(null);

const confirmDelete = ({ session_id, name }) => {
    confirm.require({
        message: t('delete_session.delete_message', { name }),
        header: t('delete_session.delete_title'),
        icon: 'pi pi-info-circle',
        rejectLabel: t('form.cancel'),
        rejectProps: {
            label: t('form.cancel'),
            severity: 'secondary',
            outlined: true,
        },
        acceptProps: {
            label: t('form.delete'),
            severity: 'danger',
        },
        accept: async () => {
            try {
                await deleteSession(session_id);

                sessionStore.removeSessionFromList(session_id);

                toast.add({ severity: 'success', summary: t('delete_session.delete_title'), detail: t('delete_session.delete_session_success'), life: 3000 });
                await sessionStore.getSessions();
            } catch (error) {
                toast.add({ severity: 'error', summary: t('delete_session.delete_title'), detail: error, life: 3000 });
            }
        },
        reject: () => {},
    });
};

const toggleCreateSessionModal = () => {
    if (!createModalShow.value) {
        editForm.value = false;
        editSessionId.value = null;
    }

    createModalShow.value = !createModalShow.value;
};

const openEditSession = (session_id) => {
    editForm.value = true;
    editSessionId.value = session_id;

    createModalShow.value = true;
};

const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
        toast.add({ severity: 'success', summary: t('global.copied'), life: 3000 });
    });
};

watch(
    () => route.query,
    () => {
        if (route.query.id) {
            openEditSession(route.query.id);
        }
    },
    { immediate: true }
);
</script>

<template>
    <Suspense>
        <template #default>
            <SessionForm v-if="createModalShow" v-model:visible="createModalShow" @hide="toggleCreateSessionModal" :editForm="editForm" :session_id="editSessionId" />
        </template>
        <template #fallback>
            <div class="flex items-center justify-center h-full">
                <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="8" fill="transparent" animationDuration=".5s" aria-label="Custom ProgressSpinner" />
            </div>
        </template>
    </Suspense>

    <DataTable :value="sessionsList" class="user-table">
        <template #empty> {{ t('global.noData') }}</template>

        <template #header>
            <div class="flex items-center gap-x-4 justify-between">
                <span class="text-xl font-bold">{{ t('global.sessions') }}</span>
                <Button icon="pi pi-plus" :label="$t('create_session.create_session')" @click="toggleCreateSessionModal" v-if="!isModerator" />
            </div>
        </template>

        <Column field="name" :header="t('global.sessionName')">
            <template #body="{ data }">
                <RouterLink :to="{ path: 'session/' + data.session_id }">
                    <h4 class="user-name">{{ data.name }}</h4>
                </RouterLink>
            </template>
        </Column>

        <Column field="id" :header="t('global.sessions')">
            <template #body="{ data }">
                <RouterLink :to="{ path: 'session/' + data.session_id }">
                    <h4 class="user-name">{{ data.session_id }}</h4>
                </RouterLink>
            </template>
        </Column>

        <Column field="id" :header="t('global.actions')" v-if="!isModerator">
            <template #body="{ data }">
                <div class="session-actions" v-if="data.is_owner || isSuperAdmin">
                    <Button icon="pi pi-copy" variant="outlined" @click="copyToClipboard(data.session_id)" />
                    <Button icon="pi pi-pen-to-square" severity="info" variant="outlined" @click="openEditSession(data.session_id)" />
                    <Button icon="pi pi-trash" severity="danger" variant="outlined" @click="confirmDelete(data)" />
                </div>
            </template>
        </Column>
    </DataTable>
    <ConfirmDialog></ConfirmDialog>
</template>

<style scoped>
.user-table {
    @apply text-sm;
}

.dialog-footer-actions {
    @apply flex items-center gap-x-4 justify-end pt-4;
}

.session-actions {
    @apply flex items-center gap-x-1;
}
</style>
